import React, { useEffect, useState, lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Container, Col, Row } from 'components/layout'
import { TextInput } from 'components/textInput/TextInput'
import { Password } from 'components/password/Password'
import { Button } from 'components/button/Button'
import { Spinner } from 'components/spinner/Spinner'
import { ToggleButton } from 'components/toggleButton/ToggleButton'
import {
    useOnChange,
    useBindDispatch,
    useAnalytics,
    useAppSelector
} from 'hooks'
import { constants, isAuthenticated } from 'helpers'
import './login.scss'
import { AuthMethodEnum } from 'storeTypes'
import { useBaseNavigation } from 'hooks/useBaseNavigation'

// Lazy load assets and Google OAuth (defer until after initial render)
const HandShakeLogo = lazy(() =>
    import('assets').then((module) => ({ default: module.HandShakeLogo }))
)
const GoogleLoginButton = lazy(() =>
    import('../../components/button/GoogleLoginButton').then((module) => ({
        default: module.GoogleLoginButton
    }))
)

export const Login = () => {
    const { t } = useTranslation()
    const [error, setError] = useState(false)
    const [isSpinner, setIsSpinner] = useState(false)
    const [errMsg, setErrMsg] = useState('')
    const navigate = useBaseNavigation()
    const authenticated = isAuthenticated()
    const { inputText, onChangeHandler, resetTextInput } = useOnChange({
        username: '',
        password: ''
    })
    const { loginDispatch, navbarHandler } = useBindDispatch()
    const { response, loggingIn } = useAppSelector((store) => store.auth)
    const { updateUserInfo } = useAnalytics()

    const isTrainingModeOn =
        process.env.REACT_APP_CONNECT_TRAINING_MODE === 'true'

    // hide menu for login page
    useEffect(() => {
        navbarHandler(
            authenticated ? constants.SHOW_NAVBAR : constants.HIDE_NAVBAR
        )
    }, [])

    // don't show login page for authenticated users
    useEffect(() => {
        if (authenticated) {
            navigate('/home')
        }
    }, [])

    useEffect(() => {
        setIsSpinner(loggingIn ?? false)
        if (response?.statusCode === 200 && response?.user) {
            updateUserInfo(response.user.username, response.user.role)
            localStorage.setItem(
                constants.USER_LOGIN_TIMESTAMP,
                Date.now().toString()
            )
            navigate('/home')
            resetTextInput()
            // show menu for logged in user
            navbarHandler(constants.SHOW_NAVBAR)
        } else if (response?.statusCode && response.statusCode >= 400) {
            errorHandler(response.message)
        }
    }, [response, loggingIn])

    const handleGoogleLogin = ({
        access_token: accessToken
    }: {
        access_token: string
    }) => {
        loginDispatch({
            method: AuthMethodEnum.GOOGLE,
            methodData: { id_token: accessToken }
        })
    }

    // handle login data
    const loginHandler = (e: React.FormEvent<HTMLFormElement>) => {
        const { username, password } = inputText
        e.preventDefault()
        if (!username || !password) {
            errorHandler(t('login.noInput'))
            return
        }
        setError(false)
        setErrMsg('')
        loginDispatch({
            method: AuthMethodEnum.PASSWORD,
            methodData: { ...inputText }
        })
    }

    // no input form validation
    const errorHandler = (message: string) => {
        setError(true)
        setErrMsg(t(message))
    }

    const handleTrainingToggle = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        window.location.href = event.target.checked
            ? process.env.REACT_APP_TRAINING_URL ?? ''
            : process.env.REACT_APP_PRODUCTION_BASE_URL ?? ''
    }

    const loginButtonEnabled =
        inputText.username?.length && inputText.password?.length && !loggingIn

    return (
        <div>
            <Container className="login-container">
                <Row>
                    <Col md={12} className="login-top-section">
                        <div className="training-toggle">
                            <ToggleButton
                                toggleText={t('train').toUpperCase()}
                                className="toggle-training"
                                onToggle={handleTrainingToggle}
                                defaultValue={isTrainingModeOn ? 'on' : 'off'}
                            />
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        {error && <p className="login-error">{errMsg}</p>}
                        <div className="logo-parent">
                            <Suspense fallback={<div style={{ height: 60 }} />}>
                                <HandShakeLogo />
                            </Suspense>
                            <h2 className="header-welcome login-welcome">
                                {t('login.welcomeHeader')}
                            </h2>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <div className="form-parent login-form">
                            <form onSubmit={loginHandler}>
                                <div className="formgroup">
                                    <TextInput
                                        id="username"
                                        name="username"
                                        value={inputText.username}
                                        onChange={onChangeHandler}
                                        placeholder={t(
                                            'login.usernamePlaceholder'
                                        )}
                                        className={`login-input${
                                            error
                                                ? '--invalid'
                                                : inputText.username?.length > 0
                                                ? '--valid'
                                                : ''
                                        }`}
                                    />
                                </div>
                                <div className="formgroup">
                                    <Password
                                        icon
                                        id="password"
                                        name="password"
                                        value={inputText.password}
                                        placeholder={t(
                                            'login.passwordPlaceholder'
                                        )}
                                        onChange={onChangeHandler}
                                        className={`login-input${
                                            error
                                                ? '--invalid'
                                                : inputText.password?.length > 0
                                                ? '--valid'
                                                : ''
                                        }`}
                                    />
                                </div>
                                <div className="formgroup">
                                    <Button
                                        style="primary"
                                        size="large"
                                        className="login-button"
                                        disable={!loginButtonEnabled}
                                        id="login-button"
                                    >
                                        {isSpinner ? (
                                            <Spinner size="40" />
                                        ) : (
                                            t('login.login')
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </Col>
                </Row>
            </Container>
            <div className="fc-section">
                <h2 className="fc-title">{t('login.fc-title')}</h2>
                <div className="formgroup">
                    <Suspense
                        fallback={
                            <Button
                                ariaLabel="google-signin"
                                className="google-btn"
                                disable
                            >
                                {t('login.googleLogin')}
                            </Button>
                        }
                    >
                        <GoogleLoginButton onGoogleLogin={handleGoogleLogin} />
                    </Suspense>
                </div>
            </div>
        </div>
    )
}
